import React, { useState } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { IoAddSharp } from 'react-icons/io5';
import ImageUploader from './../../../../ImageUpload/ImageUploader';
import { Image } from './Image';
import { createImageObject } from '../lib/imageHelpers';
import { calculateSortOrderRank, rebalanceSortOrderRanks } from '../lib/sortOrderHelpers';

const MAX_IMAGES_BEFORE_TOP_ADD_BUTTON = 6;

export const Images = ({ images, setImages, disabled = false }) => {
  const sensors = useSensors(useSensor(PointerSensor));
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [insertionPosition, setInsertionPosition] = useState('end');

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      setImages(prev => {
        const oldIndex = prev.findIndex(item => item.id === active.id);
        const newIndex = prev.findIndex(item => item.id === over.id);

        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleRemoveImage = id => {
    setImages(prev => prev.filter(image => image.id !== id));
  };

  const handleMakeFeatured = id => {
    setImages(prev =>
      prev.map(image => {
        if (image.id === id) {
          return { ...image, metadata: { ...image.metadata, featured: true } };
        }

        return { ...image, metadata: { ...image.metadata, featured: false } };
      })
    );
  };

  const handleMakeCover = id => {
    setImages(prev =>
      prev.map(image => {
        if (image.id === id) {
          return { ...image, metadata: { ...image.metadata, cover: true } };
        }

        return { ...image, metadata: { ...image.metadata, cover: false } };
      })
    );
  };

  const handleAddImage = newImage => {
    setImages(prev => {
      const position = insertionPosition === 'start' ? 0 : prev.length;
      const imageObject = createImageObject(newImage, prev);
      const sortOrderRank = calculateSortOrderRank(prev, position, true);

      /**
       * Rebalance images when we couldn't get a sortOrderRank for the new image.
       *
       * A new image should be inserted at the beginning or end, so we should never actually need to trigger
       * rebalancing, but since calculateSortOrderRank can return null, we need to handle that case.
       */
      if (sortOrderRank === null) {
        const finalImageObject = {
          ...imageObject,
          metadata: {
            ...imageObject.metadata,
            sortOrderRank: 0
          }
        };

        const newImages = insertionPosition === 'start' ? [finalImageObject, ...prev] : [...prev, finalImageObject];
        return rebalanceSortOrderRanks(newImages);
      }

      const finalImageObject = {
        ...imageObject,
        metadata: {
          ...imageObject.metadata,
          sortOrderRank
        }
      };

      return insertionPosition === 'start' ? [finalImageObject, ...prev] : [...prev, finalImageObject];
    });
  };

  const handleReplaceImage = newImage => {
    setImages(prev => {
      return prev.map(image => (image.id === newImage.id ? newImage : image));
    });
  };

  const handleChangeImageStyle = (id, style) => {
    setImages(prev => prev.map(image => (image.id === id ? { ...image, metadata: { ...image.metadata, style } } : image)));
  };

  return (
    <>
      {images.length > MAX_IMAGES_BEFORE_TOP_ADD_BUTTON && (
        <button
          className='add-image-button'
          onClick={() => {
            setInsertionPosition('start');
            setShowImageUploader(true);
          }}
          disabled={disabled}
        >
          <IoAddSharp size={24} />
          <span>Add Image</span>
        </button>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={images} strategy={rectSortingStrategy}>
          <div className='images'>
            {images.map((image, index) => (
              <Image
                key={image.id}
                image={image}
                index={index}
                isFeatured={image.metadata.featured}
                isCover={image.metadata.cover}
                style={image.metadata.style}
                onRequestRemove={handleRemoveImage}
                onRequestMakeFeatured={handleMakeFeatured}
                onRequestMakeCover={handleMakeCover}
                onRequestReplaceImage={handleReplaceImage}
                onRequestChangeImageStyle={handleChangeImageStyle}
                disabled={disabled}
              />
            ))}

            <button
              className='add-image'
              onClick={() => {
                setInsertionPosition('end');
                setShowImageUploader(true);
              }}
              disabled={disabled}
            >
              <IoAddSharp size={24} />
              <span>Upload Image</span>
            </button>
          </div>
        </SortableContext>
      </DndContext>
      <ImageUploader isVisible={showImageUploader} setIsVisible={setShowImageUploader} onSaveCallback={handleAddImage} />
    </>
  );
};
